import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  useHardDeletePensionContract,
  usePensionContract,
  useSoftDeletePensionContract,
  useUpdatePensionContract,
} from "../hooks/pension";
import type { PensionContractKind, UpdatePensionContractInput } from "../lib/pension";

const KINDS: PensionContractKind[] = ["GESETZLICH", "BAV", "RIESTER", "RUERUP", "PRIVAT"];

function formatEur(cents: number | null): string {
  if (cents === null) return "—";
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("de-DE", { timeZone: "UTC" });
}

export function PensionDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const detail = usePensionContract(id ?? "");
  const update = useUpdatePensionContract();
  const softDelete = useSoftDeletePensionContract();
  const hardDelete = useHardDeletePensionContract();
  const [editMode, setEditMode] = useState(false);

  const [name, setName] = useState("");
  const [kind, setKind] = useState<PensionContractKind>("PRIVAT");
  const [provider, setProvider] = useState("");
  const [contractNumber, setContractNumber] = useState("");
  const [startDate, setStartDate] = useState("");
  const [monthlyContributionEur, setMonthlyContributionEur] = useState("");
  const [expectedRetirementAge, setExpectedRetirementAge] = useState("");
  const [projectedMonthlyPensionEur, setProjectedMonthlyPensionEur] = useState("");
  const [notes, setNotes] = useState("");

  if (detail.isLoading || !detail.data) {
    return (
      <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
        …
      </div>
    );
  }
  const c = detail.data;

  function enterEdit() {
    setName(c.name);
    setKind(c.kind);
    setProvider(c.provider ?? "");
    setContractNumber(c.contractNumber ?? "");
    setStartDate(c.startDate);
    setMonthlyContributionEur(
      c.monthlyContributionCents !== null ? String(c.monthlyContributionCents / 100) : "",
    );
    setExpectedRetirementAge(
      c.expectedRetirementAge !== null ? String(c.expectedRetirementAge) : "",
    );
    setProjectedMonthlyPensionEur(
      c.projectedMonthlyPensionCents !== null ? String(c.projectedMonthlyPensionCents / 100) : "",
    );
    setNotes(c.notes ?? "");
    setEditMode(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    const input: UpdatePensionContractInput = {
      name: name.trim(),
      kind,
      provider: provider.trim() || null,
      contractNumber: contractNumber.trim() || null,
      startDate,
      monthlyContributionCents: monthlyContributionEur
        ? Math.round(Number(monthlyContributionEur) * 100)
        : null,
      expectedRetirementAge: expectedRetirementAge ? Number(expectedRetirementAge) : null,
      projectedMonthlyPensionCents: projectedMonthlyPensionEur
        ? Math.round(Number(projectedMonthlyPensionEur) * 100)
        : null,
      notes: notes.trim() || null,
    };
    await update.mutateAsync({ id, input });
    setEditMode(false);
  }

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs mb-2" style={{ color: "var(--muted)" }}>
            <Link to="/pension" className="hover:underline">
              {t("nav.pension")}
            </Link>
            <span>/</span>
            <span style={{ color: "var(--text)" }}>{c.name}</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{c.name}</h1>
          <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>
            {t(`pension.kind.${c.kind}`)}
            {c.provider && ` · ${c.provider}`}
          </p>
        </div>
        <div className="flex gap-2">
          {!editMode && (
            <button
              type="button"
              className="px-3 py-2 rounded-lg border text-sm font-medium"
              style={{ borderColor: "var(--border)", background: "var(--card)" }}
              onClick={enterEdit}
            >
              {t("pension.detail.edit")}
            </button>
          )}
          {c.isActive && !editMode && (
            <button
              type="button"
              className="px-3 py-2 rounded-lg border text-sm font-medium"
              style={{ borderColor: "var(--border)", background: "var(--card)" }}
              onClick={() => softDelete.mutate(c.id)}
            >
              {t("pension.detail.deactivate")}
            </button>
          )}
          {!editMode && (
            <button
              type="button"
              className="px-3 py-2 rounded-lg border text-sm font-medium"
              style={{ borderColor: "var(--border)", background: "var(--card)", color: "var(--neg)" }}
              onClick={async () => {
                if (window.confirm(t("pension.detail.confirm_delete"))) {
                  await hardDelete.mutateAsync(c.id);
                  nav("/pension");
                }
              }}
            >
              {t("pension.detail.delete")}
            </button>
          )}
        </div>
      </div>

      {!editMode ? (
        <div className="fv-card p-5 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <Field label={t("pension.detail.start_date")}>{formatDate(c.startDate)}</Field>
          <Field label={t("pension.detail.contract_number")}>{c.contractNumber ?? "—"}</Field>
          <Field label={t("pension.detail.expected_retirement_age")}>
            {c.expectedRetirementAge ?? "—"}
          </Field>
          <Field label={t("pension.detail.monthly_contribution")}>
            {formatEur(c.monthlyContributionCents)}
          </Field>
          <Field label={t("pension.detail.projected_pension")}>
            {formatEur(c.projectedMonthlyPensionCents)}
          </Field>
          <Field label={t("pension.detail.status")}>
            {c.isActive ? t("pension.detail.active") : t("pension.detail.inactive")}
          </Field>
          {c.notes && (
            <div className="col-span-2 md:col-span-3">
              <p className="fv-kpi-label">{t("pension.detail.notes")}</p>
              <p className="mt-1 whitespace-pre-wrap text-sm">{c.notes}</p>
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={handleSave} className="fv-card p-5 space-y-3">
          <Field label={t("pension.form.name")}>
            <input
              type="text"
              required
              className="border rounded px-2 py-1 w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("pension.form.kind")}>
              <select
                className="border rounded px-2 py-1 w-full"
                value={kind}
                onChange={(e) => setKind(e.target.value as PensionContractKind)}
              >
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`pension.kind.${k}`)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t("pension.form.provider")}>
              <input
                type="text"
                className="border rounded px-2 py-1 w-full"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
              />
            </Field>
          </div>
          <Field label={t("pension.form.contract_number")}>
            <input
              type="text"
              className="border rounded px-2 py-1 w-full"
              value={contractNumber}
              onChange={(e) => setContractNumber(e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("pension.form.start_date")}>
              <input
                type="date"
                required
                className="border rounded px-2 py-1 w-full"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Field>
            <Field label={t("pension.form.expected_retirement_age")}>
              <input
                type="number"
                min={50}
                max={80}
                className="border rounded px-2 py-1 w-full"
                value={expectedRetirementAge}
                onChange={(e) => setExpectedRetirementAge(e.target.value)}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("pension.form.monthly_contribution")}>
              <input
                type="number"
                step="0.01"
                min={0}
                className="border rounded px-2 py-1 w-full"
                value={monthlyContributionEur}
                onChange={(e) => setMonthlyContributionEur(e.target.value)}
              />
            </Field>
            <Field label={t("pension.form.projected_pension")}>
              <input
                type="number"
                step="0.01"
                min={0}
                className="border rounded px-2 py-1 w-full"
                value={projectedMonthlyPensionEur}
                onChange={(e) => setProjectedMonthlyPensionEur(e.target.value)}
              />
            </Field>
          </div>
          <Field label={t("pension.form.notes")}>
            <textarea
              className="border rounded px-2 py-1 w-full"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Field>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={update.isPending}
              className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
            >
              {t("pension.form.save")}
            </button>
            <button
              type="button"
              className="px-3 py-1 border rounded"
              onClick={() => setEditMode(false)}
            >
              {t("pension.form.cancel")}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="fv-kpi-label">{label}</p>
      <div className="mono mt-1 text-sm" style={{ color: "var(--text)" }}>
        {children}
      </div>
    </div>
  );
}
